import { useState } from 'react';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { FieldError } from '../../../../../components/novarium/NvTableParts';
import { SelectOption } from '../../../types/billing.types';

export interface NewCheck {
  number: string;
  bank: string;
  amount: number;
  paymentDate: Date;
  description: string;
  status: number;
  destination: number;
}

interface CheckDialogProps {
  statuses: SelectOption[];
  destinations: SelectOption[];
  onCancel: () => void;
  onAdd: (check: NewCheck) => void;
}

type Errors = Partial<Record<'number' | 'bank' | 'amount' | 'paymentDate' | 'status' | 'destination', string>>;

/** "Nuevo cheque", con los campos de {@code CheckWindow}. Se graba junto con el recibo. */
export default function CheckDialog({ statuses, destinations, onCancel, onAdd }: CheckDialogProps) {
  const [number, setNumber] = useState('');
  const [bank, setBank] = useState('');
  const [amount, setAmount] = useState<number | null>(null);
  const [paymentDate, setPaymentDate] = useState<Date | null>(null);
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<number | null>(statuses.find((s) => s.isDefault)?.id ?? null);
  const [destination, setDestination] = useState<number | null>(destinations.find((d) => d.isDefault)?.id ?? null);
  const [errors, setErrors] = useState<Errors>({});

  const add = () => {
    const next: Errors = {};
    if (!number.trim()) next.number = 'Ingresá el número de cheque';
    if (!bank.trim()) next.bank = 'Ingresá el nombre del banco';
    if (amount == null || amount <= 0) next.amount = 'Ingresá el importe del cheque';
    if (!paymentDate) next.paymentDate = 'Ingresá la fecha de cobro';
    if (status == null) next.status = 'Elegí el estado';
    if (destination == null) next.destination = 'Elegí el destino';
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    onAdd({ number: number.trim(), bank: bank.trim(), amount: amount!, paymentDate: paymentDate!, description: description.trim(), status: status!, destination: destination! });
  };

  const clear = (field: keyof Errors) => setErrors((e) => ({ ...e, [field]: undefined }));

  return (
    <Dialog
      header="Nuevo cheque"
      visible
      onHide={onCancel}
      className="nv-scope nv-dialog"
      modal
      draggable={false}
      footer={(
        <>
          <Button type="button" className="buttonMain2" outlined label="Cancelar" onClick={onCancel} />
          <Button type="button" className="buttonMain" label="Agregar" onClick={add} />
        </>
      )}
    >
      <div className="nv-dialog-body">
        <div className="nv-form-row">
          <div className="nv-field nv-w-s">
            <label htmlFor="check-number">Número de cheque <span className="nv-req">*</span></label>
            <InputText id="check-number" value={number} maxLength={255} onChange={(e) => { setNumber(e.target.value); clear('number'); }}
              className={errors.number ? 'p-invalid' : undefined} />
            <FieldError id="check-number-error" message={errors.number} />
          </div>
          <div className="nv-field nv-w-m">
            <label htmlFor="check-bank">Banco <span className="nv-req">*</span></label>
            <InputText id="check-bank" value={bank} maxLength={255} onChange={(e) => { setBank(e.target.value); clear('bank'); }}
              className={errors.bank ? 'p-invalid' : undefined} />
            <FieldError id="check-bank-error" message={errors.bank} />
          </div>
        </div>
        <div className="nv-form-row">
          <div className="nv-field nv-w-s">
            <label htmlFor="check-amount">Importe <span className="nv-req">*</span></label>
            <InputNumber inputId="check-amount" value={amount} mode="currency" currency="ARS" locale="es-AR" minFractionDigits={2}
              maxFractionDigits={2} min={0} onValueChange={(e) => { setAmount(e.value ?? null); clear('amount'); }}
              className={errors.amount ? 'p-invalid' : undefined} />
            <FieldError id="check-amount-error" message={errors.amount} />
          </div>
          <div className="nv-field nv-w-s">
            <label htmlFor="check-date">Fecha de cobro <span className="nv-req">*</span></label>
            <Calendar panelClassName="nv-scope" inputId="check-date" value={paymentDate} dateFormat="dd/mm/yy" showIcon
              onChange={(e) => { setPaymentDate((e.value as Date) ?? null); clear('paymentDate'); }}
              className={errors.paymentDate ? 'p-invalid' : undefined} />
            <FieldError id="check-date-error" message={errors.paymentDate} />
          </div>
          <div className="nv-field nv-w-s">
            <label htmlFor="check-status">Estado <span className="nv-req">*</span></label>
            <Dropdown panelClassName="nv-scope" inputId="check-status" value={status} options={statuses} optionLabel="name" optionValue="id" placeholder="Seleccionar"
              onChange={(e) => { setStatus(e.value ?? null); clear('status'); }} className={errors.status ? 'p-invalid' : undefined} />
            <FieldError id="check-status-error" message={errors.status} />
          </div>
          <div className="nv-field nv-w-s">
            <label htmlFor="check-destination">Destino <span className="nv-req">*</span></label>
            <Dropdown panelClassName="nv-scope" inputId="check-destination" value={destination} options={destinations} optionLabel="name" optionValue="id" placeholder="Seleccionar"
              onChange={(e) => { setDestination(e.value ?? null); clear('destination'); }} className={errors.destination ? 'p-invalid' : undefined} />
            <FieldError id="check-destination-error" message={errors.destination} />
          </div>
        </div>
        <div className="nv-field nv-w-l">
          <label htmlFor="check-description">Descripción</label>
          <InputText id="check-description" value={description} maxLength={255} onChange={(e) => setDescription(e.target.value)} />
        </div>
      </div>
    </Dialog>
  );
}
